/** Chart only reported measurements; missing telemetry is not zero consumption. */
export function flowSeries(rows: any[], imperial = false, bucketWidth = 0, now = Date.now(), cumulative = false) {
  const points = new Map<number, any>();
  for (const row of rows) {
    const time = Date.parse(row.timestamp ?? row.ingested_at ?? row.bucket ?? row.time);
    if (Number.isFinite(time)) points.set(time, row);
  }
  const sorted = [...points].sort(([a], [b]) => a - b);
  const raw = sorted.some(([, row]) => row.total_flow != null);
  let total: number | null = null;
  let runningTotal = Number(sorted[0]?.[1]?._initialDialOffset) || 0;
  const result = sorted.map(([time, row]) => {
    const rate = row.flow_rate ?? row.avg_flow_rate ?? row.value;
    let startTotal: number | null = null;
    let endTotal: number | null = null;
    if (raw) {
      total = row.total_flow ?? null;
      startTotal = total;
      endTotal = total;
    } else if (row.hourly_consumption != null || row.daily_consumption != null || row.consumption_liters != null) {
      const consumption = row.hourly_consumption ?? row.daily_consumption ?? row.consumption_liters ?? 0;
      if (cumulative) {
        startTotal = runningTotal;
        runningTotal += Number(consumption) || 0;
        endTotal = runningTotal;
        total = endTotal;
      } else {
        total = consumption;
        startTotal = consumption;
        endTotal = consumption;
      }
    } else {
      total = null;
    }
    return {
      time,
      rate: rate == null ? null : rate * (imperial ? 1 / (3.785411784 * 60) : 1),
      total: total == null ? null : total * (imperial ? 1 / 3.785411784 : 1),
      startTotal: startTotal == null ? null : startTotal * (imperial ? 1 / 3.785411784 : 1),
      endTotal: endTotal == null ? null : endTotal * (imperial ? 1 / 3.785411784 : 1),
    };
  });
  if (raw || !bucketWidth || !rows.some(row => 'hourly_consumption' in row || 'daily_consumption' in row || 'consumption_liters' in row)) return result;
  // Aggregates describe an interval, not an instantaneous sample at its start.
  return result.flatMap((point, index) => {
    const end = Math.min(point.time + bucketWidth, now);
    const startPt = { ...point, total: cumulative ? point.startTotal : point.total };
    const fraction = (bucketWidth > 0 && end > point.time) ? Math.min(1, Math.max(0, (end - point.time) / bucketWidth)) : 1;
    const proratedTotal = (cumulative && point.endTotal != null && point.startTotal != null)
      ? point.startTotal + (point.endTotal - point.startTotal) * fraction
      : (cumulative ? point.endTotal : point.total);
    const endPt = { ...point, time: Math.max(point.time, end - 1), total: proratedTotal };
    const points = [startPt, endPt];
    if (result[index + 1] && result[index + 1].time > end) {
      points.push({ time: end, rate: null, total: null, startTotal: null, endTotal: null });
    }
    return points;
  });
}

/** Unknown onset/clearance stays unknown; never invent a one-hour leak. */
export function leakInterval(alarm: any, now = Date.now()) {
  const detected = Date.parse(alarm.detected_at ?? alarm.last_seen);
  if (!Number.isFinite(detected)) return null;
  const duration = alarm.leak_duration_s;
  const hasStart = typeof duration === 'number' && Number.isFinite(duration) && duration > 0;
  const start = hasStart ? detected - duration * 1000 : detected;
  const clear = Date.parse(alarm.cleared_at);
  const active = alarm.status === 'active' || alarm.status === 'new';
  const end = Number.isFinite(clear) && clear >= detected ? clear : active ? Math.max(now, detected) : detected;
  return { start, detected, end, hasStart, active, hasClear: Number.isFinite(clear) && clear >= detected };
}

/** Include recovery rows: filtering them first collapses repeated identical alarms. */
export function leakEpisodes(statusRows: any[], details: any[] = []) {
  const episodes: any[] = [];
  let active: any = null;
  const rows = [...statusRows].sort((a, b) => Date.parse(a.ingested_at) - Date.parse(b.ingested_at));
  for (const row of rows) {
    if (row.flow_alarm == null || row.flow_alarm < 0 || !Number.isFinite(Date.parse(row.ingested_at))) continue;
    if (row.flow_alarm > 0) {
      if (!active) {
        // Metadata is associated with its detection, never copied from a different episode.
        const detail = details.filter(a => a.log_type === 'hardware' && a.flow_alarm > 0)
          .find(a => Math.abs(Date.parse(a.detected_at ?? a.last_seen) - Date.parse(row.ingested_at)) < 2000);
        active = { ...detail, log_type: 'hardware', anomaly_type: 'Hardware Leak',
          detected_at: row.ingested_at, last_seen: row.ingested_at,
          status: 'active', cleared_at: undefined, severity: 'critical',
          flow_alarm: row.flow_alarm, auto_shutoff: !!(row.flow_alarm & 128) };
        episodes.push(active);
      }
      active.last_seen = row.ingested_at;
      active.flow_alarm |= row.flow_alarm;
      active.auto_shutoff ||= !!(row.flow_alarm & 128);
      active.flow_alarm_flags = [[1, 'slow'], [2, 'overflow'], [4, 'burst']]
        .filter(([bit]) => active.flow_alarm & Number(bit)).map(([, name]) => name);
    } else if (active) {
      active.status = 'cleared';
      active.cleared_at = row.ingested_at;
      active = null;
    }
  }
  return episodes;
}
